// src/components/AppShell.tsx — Nexus-themed shell
// Rendered ONLY for authenticated + permitted sessions; the auth gate in
// App.tsx shows the landing/denied pages otherwise, so nothing about the
// admin surface leaks to signed-out visitors.
import { ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { Sigil } from './Sigil';
import type { SessionInfo } from '@eotir/oidcbridge-shared/api-contracts';

interface Props {
  user: SessionInfo;
  children: ReactNode;
}

export function AppShell({ user, children }: Props) {
  return (
    <div className="min-h-screen flex flex-col bg-bg text-ink">
      {/* Header */}
      <header className="border-b border-line bg-bg-1 px-7 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Sigil size={30} />
          <div className="leading-tight">
            <div className="font-display font-bold text-[16px] tracking-[0.04em] uppercase text-ink">
              OIDC Bridge Admin
            </div>
            <div className="font-mono text-[9.5px] tracking-eyebrow uppercase text-ink-3 mt-0.5">
              Authentication Bridge · auth-admin.eotir.com
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3.5 font-mono text-[10px] tracking-label uppercase">
          <span className="text-ink">{user.name}</span>
          {user.isSuperAdmin && <span className="eo-chip eo-chip--warn">Super-Admin</span>}
          <span className="text-ink-4">·</span>
          <a href="/api/auth/logout" className="text-crimson-hover no-underline hover:text-white">
            Sign out
          </a>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 flex">
        <Sidebar />
        <main className="flex-1 px-8 py-6 overflow-y-auto">{children}</main>
      </div>

      {/* Footer */}
      <footer className="border-t border-line bg-bg-1 px-7 py-2.5 font-mono text-[10px] tracking-label uppercase text-ink-4">
        EOTIR · OIDC Bridge · {new Date().getFullYear()}
      </footer>
    </div>
  );
}
